import React, { useRef, useEffect } from 'react';
import { Mic, MicOff, Video, VideoOff, Crown } from 'lucide-react';

const VideoGrid = ({ participants, localStream, remoteStreams, currentUserId }) => {
  const videoRefs = useRef({});

  // Combine local and remote participants for display
  const allParticipants = participants.map(p => {
    const participantId = p.userId || p.id;
    const stream = participantId === currentUserId ? localStream : remoteStreams[participantId];
    return {
      ...p,
      isLocal: participantId === currentUserId,
      stream: stream
    };
  });

  // Debug logging
  useEffect(() => {
    console.log('VideoGrid - Participants:', allParticipants.length);
    console.log('VideoGrid - Remote streams:', Object.keys(remoteStreams));
    allParticipants.forEach(p => {
      console.log(`Participant ${p.userName || p.userId}:`, {
        hasStream: !!p.stream,
        isLocal: p.isLocal,
        isVideoOff: p.isVideoOff,
        streamTracks: p.stream ? {
          video: p.stream.getVideoTracks().length,
          audio: p.stream.getAudioTracks().length
        } : 'no stream'
      });
    });
  }, [allParticipants, remoteStreams]);

  // Update video elements when streams change
  useEffect(() => {
    console.log('[VideoGrid] Updating video elements, remoteStreams keys:', Object.keys(remoteStreams));
    
    allParticipants.forEach(participant => {
      const participantId = participant.userId || participant.id;
      const videoEl = videoRefs.current[participantId];
      
      if (!videoEl) {
        console.log(`[VideoGrid] No video element for ${participant.userName || participantId}`);
        return;
      }

      const hasStream = !!participant.stream;
      const shouldShowVideo = hasStream && !participant.isVideoOff;
      
      console.log(`[VideoGrid] Participant ${participant.userName || participantId}:`, {
        hasStream,
        isVideoOff: participant.isVideoOff,
        shouldShowVideo,
        currentSrcObject: !!videoEl.srcObject,
        streamTracks: participant.stream ? {
          video: participant.stream.getVideoTracks().length,
          audio: participant.stream.getAudioTracks().length
        } : null
      });

      if (shouldShowVideo && participant.stream) {
        const videoTracks = participant.stream.getVideoTracks();
        console.log(`[VideoGrid] Stream for ${participant.userName || participantId} has ${videoTracks.length} video tracks`);
        
        if (videoTracks.length > 0) {
          if (videoEl.srcObject !== participant.stream) {
            console.log(`[VideoGrid] ✅ Updating srcObject for ${participant.userName || participantId}`);
            videoEl.srcObject = participant.stream;
            // Force play
            videoEl.play().catch(err => {
              console.error(`[VideoGrid] ❌ Error playing video for ${participant.userName}:`, err);
            });
          } else {
            // Stream is already set, but ensure it's playing
            if (videoEl.paused) {
              console.log(`[VideoGrid] Video paused, attempting to play for ${participant.userName || participantId}`);
              videoEl.play().catch(err => {
                console.error(`[VideoGrid] Error resuming video:`, err);
              });
            }
            // Check if video tracks are enabled
            videoTracks.forEach(track => {
              if (!track.enabled) {
                console.log(`[VideoGrid] Video track is disabled for ${participant.userName || participantId}, enabling...`);
                track.enabled = true;
              }
            });
          }
        } else {
          console.log(`[VideoGrid] ⚠️ Stream exists but no video tracks yet for ${participant.userName || participantId}`);
          // Set the stream anyway - video tracks might arrive later
          if (videoEl.srcObject !== participant.stream) {
            videoEl.srcObject = participant.stream;
          }
        }
      } else {
        // Video is off or no stream - clear srcObject
        if (videoEl.srcObject) {
          console.log(`[VideoGrid] Clearing srcObject for ${participant.userName || participantId}`);
          videoEl.srcObject = null;
        }
      }
    });
  }, [allParticipants, localStream, remoteStreams]);

  if (allParticipants.length === 0) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <p className="text-gray-400">No participants yet</p>
      </div>
    );
  }

  // Calculate grid layout based on number of participants
  const getGridClass = (count) => {
    if (count === 1) return 'grid-cols-1';
    if (count === 2) return 'grid-cols-2';
    if (count <= 4) return 'grid-cols-2';
    if (count <= 6) return 'grid-cols-3';
    return 'grid-cols-4';
  };

  return (
    <div className={`w-full h-full grid ${getGridClass(allParticipants.length)} gap-4 p-4`}>
      {allParticipants.map((participant) => {
        const participantId = participant.userId || participant.id;
        const hasStream = !!participant.stream;
        const shouldShowVideo = hasStream && !participant.isVideoOff;
        const videoEl = videoRefs.current[participantId];
        
        return (
          <div
            key={participantId}
            className="relative bg-gray-800 rounded-lg overflow-hidden aspect-video"
          >
            {/* Always render video element - update srcObject based on stream */}
            <video
              ref={(el) => {
                if (el) {
                  videoRefs.current[participantId] = el;
                  // Set stream immediately if available
                  if (participant.stream && !participant.isVideoOff) {
                    const videoTracks = participant.stream.getVideoTracks();
                    if (videoTracks.length > 0) {
                      if (el.srcObject !== participant.stream) {
                        console.log(`[VideoGrid] Setting srcObject for ${participant.userName || participantId} with ${videoTracks.length} video tracks`);
                        el.srcObject = participant.stream;
                        el.play().catch(err => {
                          console.error(`[VideoGrid] Error playing video for ${participant.userName}:`, err);
                        });
                      }
                    } else {
                      console.log(`[VideoGrid] Stream exists but no video tracks for ${participant.userName || participantId}`);
                      // Keep the stream but wait for video tracks
                      if (el.srcObject !== participant.stream) {
                        el.srcObject = participant.stream;
                      }
                    }
                  } else {
                    el.srcObject = null;
                  }
                } else {
                  delete videoRefs.current[participantId];
                }
              }}
              autoPlay
              playsInline
              muted={participant.isLocal}
              className={`w-full h-full object-cover ${shouldShowVideo ? 'block' : 'hidden'}`}
              onLoadedMetadata={(e) => {
                console.log(`[VideoGrid] Video metadata loaded for ${participant.userName || participantId}`);
                e.target.play().catch(err => {
                  console.error(`[VideoGrid] Error playing after metadata:`, err);
                });
              }}
              onPlay={() => {
                console.log(`[VideoGrid] Video playing for ${participant.userName || participantId}`);
              }}
              onError={(e) => {
                console.error(`[VideoGrid] Video error for ${participant.userName || participantId}:`, e);
              }}
              onLoadedData={(e) => {
                console.log(`[VideoGrid] Video data loaded for ${participant.userName || participantId}`);
              }}
            />
            
            {/* Placeholder - show when no video or video is off */}
            {!shouldShowVideo && (
              <div className="w-full h-full flex items-center justify-center bg-gray-700 absolute inset-0">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-gray-600 flex items-center justify-center mx-auto mb-2">
                    <span className="text-2xl text-gray-300">
                      {(participant.userName || 'U')[0].toUpperCase()}
                    </span>
                  </div>
                  <p className="text-gray-300 text-sm">{participant.userName || 'Anonymous'}</p>
                  {hasStream && participant.isVideoOff && (
                    <p className="text-red-400 text-xs mt-1">Camera off</p>
                  )}
                  {!hasStream && (
                    <p className="text-yellow-400 text-xs mt-1">Waiting for stream...</p>
                  )}
                </div>
              </div>
            )}

            {/* Participant Info Overlay */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  {participant.isHost && (
                    <Crown size={14} className="text-yellow-400" />
                  )}
                  <span className="text-white text-sm font-medium truncate">
                    {participant.userName || 'Anonymous'}
                    {participant.isLocal && ' (You)'}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  {participant.isMuted ? (
                    <MicOff size={16} className="text-red-400" />
                  ) : (
                    <Mic size={16} className="text-green-400" />
                  )}
                  {participant.isVideoOff ? (
                    <VideoOff size={16} className="text-red-400" />
                  ) : (
                    <Video size={16} className="text-green-400" />
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default VideoGrid;

